import React from "react";
import {
    Box,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    Divider,
    Button,
    Stack,
} from "@mui/material";
import { Description, Link, Phone, Message } from "@mui/icons-material";
import { WB_SX } from "../../../ui/ux/uiTokens";

const CampaignPreviewStep = ({
    selectedTemplate,
    templateDetails,
    loadingTemplateDetails,
    selectedGroup,
    parameterSource,
    customParameters,
    headerMediaId,
    headerMediaPreviewUrl,
}) => {
    if (!selectedTemplate) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">
                    Please select a template in Step 1 first.
                </Alert>
            </Box>
        );
    }

    if (loadingTemplateDetails || !templateDetails) {
        return (
            <Box
                sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    {loadingTemplateDetails
                        ? "Loading template details..."
                        : "Template details not available"}
                </Typography>
            </Box>
        );
    }

    const headerComponent = templateDetails.components?.find(
        (c) => c.type === "HEADER"
    );
    const bodyComponent = templateDetails.components?.find(
        (c) => c.type === "BODY"
    );
    const footerComponent = templateDetails.components?.find(
        (c) => c.type === "FOOTER"
    );
    const buttonsComponent = templateDetails.components?.find(
        (c) => c.type === "BUTTONS"
    );

    // Build preview text with parameters
    const replaceParameters = (text, paramArray) => {
        if (!text || !paramArray) return text || "";
        let result = text;
        paramArray.forEach((param, index) => {
            result = result.replace(
                new RegExp(`\\{\\{${index + 1}\\}\\}`, "g"),
                param || `{{${index + 1}}}`
            );
        });
        return result;
    };

    // Build parameters array based on source
    const buildParameters = () => {
        if (!templateDetails) return { header: [], body: [] };

        const headerComponent = templateDetails.components?.find(
            (c) => c.type === "HEADER"
        );
        const bodyComponent = templateDetails.components?.find(
            (c) => c.type === "BODY"
        );

        const headerParamCount =
            headerComponent && headerComponent.format === "TEXT"
                ? (headerComponent.text?.match(/\{\{(\d+)\}\}/g) || []).length
                : 0;
        const bodyParamCount = bodyComponent
            ? (bodyComponent.text?.match(/\{\{(\d+)\}\}/g) || []).length
            : 0;

        if (parameterSource === "custom") {
            // Use custom parameters
            return {
                header: customParameters.slice(0, headerParamCount) || [],
                body: customParameters.slice(headerParamCount, headerParamCount + bodyParamCount) || [],
            };
        } else {
            // Use group parameters - for preview, use sample values
            return {
                header: new Array(headerParamCount).fill("Sample Value"),
                body: new Array(bodyParamCount).fill("Sample Value"),
            };
        }
    };

    const parameters = buildParameters();

    const headerText =
        headerComponent?.format === "TEXT"
            ? replaceParameters(headerComponent.text, parameters?.header)
            : null;

    const bodyText = bodyComponent
        ? replaceParameters(bodyComponent.text, parameters?.body)
        : null;

    // WhatsApp background pattern (subtle)
    const whatsappBgPattern = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAAdgAAAHYBTnsmCAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAAAnSURBVHic7cEBDQAAAMIg+6c2BL4mAAAAAAAAAAAAAAAAAAD4B7a4AAFj8b8zAAAAAElFTkSuQmCC")`;

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" sx={{ ...WB_SX.sectionTitle, mb: 2 }}>
                WhatsApp Campaign Preview
            </Typography>

            {selectedGroup && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Group:</strong> {selectedGroup} |{" "}
                        <strong>Parameter Source:</strong> {parameterSource === "custom" ? "Custom Parameters" : "Group Parameters"}
                    </Typography>
                </Alert>
            )}

            {/* WhatsApp-style Preview Container */}
            <Paper
                elevation={3}
                sx={{
                    overflow: "hidden",
                    borderRadius: 2,
                    maxWidth: 400,
                    mx: "auto",
                    height: "auto",
                    minHeight: 500,
                }}
            >
                {/* WhatsApp Header */}
                <Box
                    sx={{
                        backgroundColor: "#075e54",
                        color: "white",
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: "#25d366",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                        }}
                    >
                        {selectedTemplate.name.charAt(0).toUpperCase()}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {selectedTemplate.name}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            WhatsApp Business
                        </Typography>
                    </Box>
                </Box>

                {/* WhatsApp Chat Area */}
                <Box
                    sx={{
                        backgroundColor: "#e5ddd5",
                        backgroundImage: whatsappBgPattern,
                        minHeight: 400,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    {/* Template Message Bubble - Received (Left side, white background) */}
                    <Card
                        sx={{
                            maxWidth: "75%",
                            mr: "auto",
                            ml: 0,
                            backgroundColor: "#ffffff",
                            borderRadius: "0 7.5px 7.5px 7.5px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                            border: "1px solid #e5e5e5",
                        }}
                    >
                        <CardContent sx={{ p: 1, "&:last-child": { pb: 1.5 } }}>
                            {/* Header (Text) */}
                            {headerComponent?.format === "TEXT" && headerText && (
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{ mb: 1, color: "#075e54" }}
                                >
                                    {headerText}
                                </Typography>
                            )}

                            {/* Header (Media) */}
                            {headerComponent &&
                                ["IMAGE", "VIDEO", "DOCUMENT"].includes(
                                    headerComponent.format
                                ) && (
                                    <Box sx={{ mb: 1 }}>
                                        {headerMediaPreviewUrl && headerMediaPreviewUrl.trim() ? (
                                            <Box>
                                                {headerComponent.format === "IMAGE" && (
                                                    <Box
                                                        component="img"
                                                        src={headerMediaPreviewUrl}
                                                        alt="Preview"
                                                        onError={(e) => {
                                                            console.error("Image preview error:", e);
                                                            e.target.style.display = "none";
                                                        }}
                                                        sx={{
                                                            width: "100%",
                                                            maxHeight: 250,
                                                            borderRadius: 1,
                                                            display: "block",
                                                            objectFit: "contain",
                                                        }}
                                                    />
                                                )}
                                                {headerComponent.format === "VIDEO" && (
                                                    <Box
                                                        component="video"
                                                        src={headerMediaPreviewUrl}
                                                        controls
                                                        sx={{
                                                            width: "100%",
                                                            maxHeight: 250,
                                                            borderRadius: 1,
                                                            display: "block",
                                                        }}
                                                    />
                                                )}
                                                {headerComponent.format === "DOCUMENT" && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            p: 1.5,
                                                            backgroundColor: "#f5f5f5",
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        <Description fontSize="large" color="primary" />
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                Document
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Ready to send
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        ) : headerMediaId ? (
                                            <Alert severity="info" sx={{ py: 0.5 }}>
                                                Media uploaded (ID: {headerMediaId})
                                            </Alert>
                                        ) : (
                                            <Alert severity="warning" sx={{ py: 0.5 }}>
                                                No media uploaded for header
                                            </Alert>
                                        )}
                                    </Box>
                                )}

                            {/* Body */}
                            {bodyText && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        lineHeight: 1.5,
                                        whiteSpace: "pre-wrap",
                                        mb: footerComponent?.text || buttonsComponent?.buttons ? 1 : 0,
                                    }}
                                >
                                    {bodyText}
                                </Typography>
                            )}

                            {/* Footer */}
                            {footerComponent?.text && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary", fontSize: "0.75rem", mt: bodyText || headerComponent ? 1 : 0 }}
                                >
                                    {footerComponent.text}
                                </Typography>
                            )}

                            {/* Buttons */}
                            {buttonsComponent?.buttons && buttonsComponent.buttons.length > 0 && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Stack spacing={1}>
                                        {buttonsComponent.buttons.map((button, index) => {
                                            // Determine icon based on button type
                                            const getButtonIcon = () => {
                                                const buttonType = button.type || "QUICK_REPLY";
                                                switch (buttonType) {
                                                    case "URL":
                                                        return <Link fontSize="small" />;
                                                    case "PHONE_NUMBER":
                                                        return <Phone fontSize="small" />;
                                                    case "QUICK_REPLY":
                                                    default:
                                                        return <Message fontSize="small" />;
                                                }
                                            };

                                            return (
                                                <Button
                                                    key={index}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    startIcon={getButtonIcon()}
                                                    sx={{
                                                        borderColor: "#25d366",
                                                        color: "#25d366",
                                                        textTransform: "none",
                                                        fontSize: "0.75rem",
                                                        justifyContent: "center",
                                                        "&:hover": {
                                                            borderColor: "#128c7e",
                                                            backgroundColor: "rgba(37, 211, 102, 0.1)",
                                                        },
                                                    }}
                                                >
                                                    {button.text || button.type}
                                                </Button>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            )}
                        </CardContent>

                        {/* Message Time - Left aligned for received messages */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                px: 1.5,
                                pb: 1,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    fontSize: "0.7rem",
                                }}
                            >
                                {new Date().toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </Typography>
                        </Box>
                    </Card>

                    {/* Info Message */}
                    <Card
                        sx={{
                            maxWidth: "80%",
                            ml: "auto",
                            backgroundColor: "#dcf8c6",
                            borderRadius: "7.5px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        }}
                    >
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary">
                                This is a preview of how your campaign message will look when
                                received by contacts in the group.
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>

                {/* WhatsApp Footer */}
                <Box
                    sx={{
                        backgroundColor: "#f0f0f0",
                        p: 1.5,
                        textAlign: "center",
                        borderTop: "1px solid #e5e5e5",
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        Template message • No replies
                    </Typography>
                </Box>
            </Paper>

            {/* Template Info */}
            <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2 }}>
                <Chip
                    label={`Category: ${selectedTemplate.category}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
                <Chip
                    label={`Language: ${selectedTemplate.language}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
                <Chip
                    label={`Status: ${selectedTemplate.status}`}
                    size="small"
                    color={selectedTemplate.status === "APPROVED" ? "success" : "default"}
                    variant="outlined"
                />
            </Box>
        </Box>
    );
};

export default CampaignPreviewStep;

